import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { PageHeader } from '@/components/shell/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RevealKeyDialog } from '@/components/shell/reveal-key-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  useContextTree,
  useCreateWriteKey,
  useDeleteApp,
  useRevokeWriteKey,
  useSetSdkEnabled,
  useWriteKeys,
  type Environment,
  type WriteKey,
} from '@/lib/queries';
import { useAuthStore } from '@/stores/auth';
import { useContextStore } from '@/stores/context';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Plus, Smartphone, Trash2, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/_authed/settings')({ component: SettingsPage });

function SettingsPage(): React.JSX.Element {
  return (
    <div className="space-y-6">
      <PageHeader
        title="Settings"
        description="Manage SDK access, delivery environments, and your PersonalizeHQ account."
      />

      <Tabs defaultValue="keys">
        <TabsList className="h-9">
          <TabsTrigger value="keys">Write keys</TabsTrigger>
          <TabsTrigger value="environments">Environments</TabsTrigger>
          <TabsTrigger value="apps">Apps</TabsTrigger>
          <TabsTrigger value="profile">Profile</TabsTrigger>
        </TabsList>

        <TabsContent value="keys" className="mt-6">
          <WriteKeysTab />
        </TabsContent>
        <TabsContent value="environments" className="mt-6">
          <EnvironmentsTab />
        </TabsContent>
        <TabsContent value="apps" className="mt-6">
          <AppsTab />
        </TabsContent>
        <TabsContent value="profile" className="mt-6">
          <ProfileTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------------------------------------------------------------------------- write keys

export function WriteKeysTab(): React.JSX.Element {
  const appId = useContextStore((s) => s.appId);
  const environmentId = useContextStore((s) => s.environmentId);
  const setEnvironment = useContextStore((s) => s.setEnvironment);
  const { data: orgs } = useContextTree();
  const { data: keys, isLoading } = useWriteKeys(environmentId);
  const create = useCreateWriteKey(environmentId);
  const revoke = useRevokeWriteKey(environmentId);

  // The raw key exists only in the create response — the server stores a hash and never returns it
  // again. Holding it in state for the length of one dialog is the entire reveal-once flow.
  const [revealed, setRevealed] = useState<{ value: string; environmentName: string } | null>(null);
  const [pendingRevoke, setPendingRevoke] = useState<{ key: WriteKey; environmentId: string } | null>(null);

  const app = orgs
    ?.flatMap((org) => org.workspaces)
    .flatMap((workspace) => workspace.apps)
    .find((candidate) => candidate.id === appId);
  const environment = app?.environments.find((candidate) => candidate.id === environmentId);
  const environmentName = environment?.name ?? 'selected';

  // A revoke confirmation only belongs to the environment where it was opened. A revealed raw key
  // is different: it exists only once, so keep it open and label its origin instead of discarding it.
  const visiblePendingRevoke = pendingRevoke?.environmentId === environmentId ? pendingRevoke.key : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-sm text-muted-foreground">Write keys authenticate the SDK against this environment.</p>
          <div className="mt-3 w-52 space-y-1.5">
            <Label htmlFor="key-environment">Environment</Label>
            <Select {...(environment ? { value: environment.id } : {})} onValueChange={setEnvironment}>
              <SelectTrigger id="key-environment" aria-label="Key environment" className="capitalize">
                <SelectValue placeholder="Select environment" />
              </SelectTrigger>
              <SelectContent>
                {app?.environments.map((candidate) => (
                  <SelectItem key={candidate.id} value={candidate.id} className="capitalize">
                    {candidate.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button
          disabled={!environmentId || create.isPending}
          onClick={() => {
            const createdFor = environmentName;
            create.mutate(undefined, {
              onSuccess: (key) => setRevealed({ value: key.key, environmentName: createdFor }),
              onError: (error: Error) => toast.error(error.message),
            })
          }}
        >
          <Plus className="size-4" />
          Create key
        </Button>
      </div>

      {isLoading ? (
        <Skeleton className="h-32 rounded-lg" />
      ) : (
        <div className="overflow-hidden rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Key ID</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-0" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {!keys?.length ? (
                <TableRow>
                  <TableCell colSpan={4} className="py-10 text-center text-sm text-muted-foreground">
                    No write keys in this environment yet.
                  </TableCell>
                </TableRow>
              ) : (
                keys.map((key) => (
                  <TableRow key={key.id}>
                    <TableCell className="font-mono text-xs">{key.id}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(key.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {key.revoked_at ? (
                        <Badge variant="secondary">Revoked</Badge>
                      ) : (
                        <Badge variant="outline">Active</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {!key.revoked_at && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => {
                            if (environmentId) setPendingRevoke({ key, environmentId });
                          }}
                        >
                          Revoke
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <RevealKeyDialog
        value={revealed?.value ?? null}
        onClose={() => setRevealed(null)}
        title={`Copy your ${revealed?.environmentName ?? ''} write key now`}
        description={`This key belongs to the ${revealed?.environmentName ?? 'selected'} environment and is shown only once. Store it securely before continuing.`}
      />

      <AlertDialog open={visiblePendingRevoke !== null} onOpenChange={(open) => !open && setPendingRevoke(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke this write key?</AlertDialogTitle>
            <AlertDialogDescription>
              Every SDK build shipping this key stops authenticating immediately, including apps already on
              devices. The key stays listed as revoked; it cannot be restored.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={() => {
                const key = visiblePendingRevoke;
                if (!key) return;
                revoke.mutate(key.id, {
                  onSuccess: () => toast.success('Write key revoked'),
                  onError: (error: Error) => toast.error(error.message),
                });
                setPendingRevoke(null);
              }}
            >
              Revoke key
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------- apps

export function AppsTab(): React.JSX.Element {
  const appId = useContextStore((s) => s.appId);
  const setApp = useContextStore((s) => s.setApp);
  const setEnvironment = useContextStore((s) => s.setEnvironment);
  const resetContext = useContextStore((s) => s.reset);
  const { data: orgs, isLoading } = useContextTree();
  const remove = useDeleteApp();
  const [pendingDelete, setPendingDelete] = useState(false);
  const [typed, setTyped] = useState('');

  const appEntries =
    orgs?.flatMap((org) =>
      org.workspaces.flatMap((workspace) => workspace.apps.map((app) => ({ app, role: org.role })))
    ) ?? [];
  const current = appEntries.find((entry) => entry.app.id === appId) ?? appEntries[0];
  const canDelete = current?.role === 'admin' || current?.role === 'editor';

  if (isLoading) return <Skeleton className="h-44 rounded-lg" />;
  if (!current) return <p className="text-sm text-muted-foreground">No app is currently selected.</p>;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex-row items-center gap-3 space-y-0">
          <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-indigo-500/10 text-indigo-600 dark:text-indigo-300">
            <Smartphone className="size-4" />
          </span>
          <div>
            <CardTitle className="text-base">{current.app.name}</CardTitle>
            <CardDescription>iOS · {current.app.environments.length} environments</CardDescription>
          </div>
        </CardHeader>
      </Card>

      <Card className="border-destructive/30">
        <CardHeader>
          <CardTitle className="text-base">Delete app</CardTitle>
          <CardDescription>
            Permanently delete this app, its experiences, audiences, campaigns, assets, environments, and write keys.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            variant="destructive"
            disabled={!canDelete}
            onClick={() => {
              setTyped('');
              setPendingDelete(true);
            }}
          >
            <Trash2 className="size-4" />
            Delete {current.app.name}
          </Button>
          {!canDelete ? <p className="mt-2 text-xs text-muted-foreground">Only an admin or editor can delete an app.</p> : null}
        </CardContent>
      </Card>

      <AlertDialog open={pendingDelete} onOpenChange={(open) => !open && setPendingDelete(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <TriangleAlert className="size-4" />
              Delete {current.app.name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the app and all delivery data beneath it. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="delete-app-confirm">
              Type <span className="font-medium text-foreground">{current.app.name}</span> to confirm
            </Label>
            <Input id="delete-app-confirm" autoComplete="off" value={typed} onChange={(event) => setTyped(event.target.value)} />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={typed !== current.app.name || remove.isPending}
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={() => {
                const deletedId = current.app.id;
                const next = appEntries.find((entry) => entry.app.id !== deletedId)?.app;
                remove.mutate(deletedId, {
                  onSuccess: () => {
                    if (next) {
                      setApp(next.id);
                      const environment = next.environments.find((candidate) => candidate.name === 'production') ?? next.environments[0];
                      if (environment) setEnvironment(environment.id);
                    } else {
                      resetContext();
                    }
                    toast.success(`${current.app.name} deleted`);
                  },
                  onError: (error: Error) => toast.error(error.message),
                });
                setPendingDelete(false);
              }}
            >
              Delete app permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------- environments

function EnvironmentsTab(): React.JSX.Element {
  const appId = useContextStore((s) => s.appId);
  const { data: orgs, isLoading } = useContextTree();
  const setSdkEnabled = useSetSdkEnabled();

  const [pendingKill, setPendingKill] = useState<Environment | null>(null);
  const [typed, setTyped] = useState('');

  const app = orgs
    ?.flatMap((org) => org.workspaces)
    .flatMap((workspace) => workspace.apps)
    .find((candidate) => candidate.id === appId);

  if (isLoading) return <Skeleton className="h-40 rounded-lg" />;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Turning the SDK off stops delivery to every device in that environment. It is a kill switch, not a
        pause.
      </p>

      <div className="divide-y rounded-lg border">
        {app?.environments.map((environment) => (
          <div key={environment.id} className="flex items-center justify-between gap-4 px-4 py-3">
            <div>
              <p className="text-sm font-medium capitalize">{environment.name}</p>
              <p className="text-xs text-muted-foreground">
                {environment.sdk_enabled ? 'SDK enabled — bundles are being served.' : 'SDK disabled — no experience reaches a device.'}
              </p>
            </div>
            <Switch
              checked={environment.sdk_enabled}
              aria-label={`SDK enabled for ${environment.name}`}
              onCheckedChange={(next) => {
                // Only the OFF direction is destructive; turning delivery back on needs no ceremony.
                if (!next) {
                  setTyped('');
                  setPendingKill(environment);
                  return;
                }
                setSdkEnabled.mutate(
                  { id: environment.id, sdkEnabled: true },
                  {
                    onSuccess: () => toast.success(`SDK enabled for ${environment.name}`),
                    onError: (error: Error) => toast.error(error.message),
                  }
                );
              }}
            />
          </div>
        ))}
        {!app?.environments.length && (
          <p className="px-4 py-10 text-center text-sm text-muted-foreground">
            This app has no environments.
          </p>
        )}
      </div>

      <AlertDialog open={pendingKill !== null} onOpenChange={(open) => !open && setPendingKill(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <TriangleAlert className="size-4" />
              Disable the SDK in {pendingKill?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Every SDK in <span className="font-medium text-foreground">{pendingKill?.name}</span> stops
              immediately. No campaign, no experience, no bundle reaches any device in this environment until
              you turn it back on. Live users see nothing.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-2">
            <Label htmlFor="kill-confirm">
              Type <span className="font-mono font-medium text-foreground">{pendingKill?.name}</span> to
              confirm
            </Label>
            <Input
              id="kill-confirm"
              autoComplete="off"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={typed !== pendingKill?.name}
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={() => {
                const environment = pendingKill;
                if (!environment) return;
                setSdkEnabled.mutate(
                  { id: environment.id, sdkEnabled: false },
                  {
                    onSuccess: () => toast.success(`SDK disabled for ${environment.name}`),
                    onError: (error: Error) => toast.error(error.message),
                  }
                );
                setPendingKill(null);
              }}
            >
              Disable the SDK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------- profile

function ProfileTab(): React.JSX.Element {
  const navigate = useNavigate();
  const { user, signOut } = useAuthStore();
  const resetContext = useContextStore((s) => s.reset);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Profile</CardTitle>
        <CardDescription>Signed in as {user?.email}</CardDescription>
      </CardHeader>
      <CardContent>
        <Button
          variant="outline"
          onClick={async () => {
            await signOut();
            resetContext();
            await navigate({ to: '/login' });
          }}
        >
          Sign out
        </Button>
      </CardContent>
    </Card>
  );
}
